import { useEffect, useMemo, useRef, useState } from "react";

// @mui
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";

// MD components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { apiFetch } from "utils/api";

function isImage(mime) {
  return (mime || "").startsWith("image/");
}

function isVideo(mime) {
  return (mime || "").startsWith("video/");
}

function normalizeTags(item) {
  if (!item) return [];
  if (Array.isArray(item.tags)) return item.tags.map((t) => String(t || "").trim()).filter(Boolean);
  if (typeof item.tags === "string") {
    return item.tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "-";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function getApiOrigin() {
  const raw = process.env.REACT_APP_SOLVIT_API_BASE || "/solvit-api";
  const trimmed = String(raw || "").trim().replace(/^["']|["']$/g, "");
  const base = trimmed || "/solvit-api";

  if (/^https?:\/\//i.test(base)) return base.replace(/\/+$/, "");

  const host = window.location.hostname;
  const isLocal = host === "localhost" || host === "127.0.0.1";
  if (isLocal && base.startsWith("/")) {
    return `http://82.165.15.24${base}`.replace(/\/+$/, "");
  }

  return base.replace(/\/+$/, "");
}

function toAbsoluteUrl(url) {
  let u = String(url || "").trim();
  if (!u) return "";

  if (/^https?:\/\//i.test(u)) return u;

  u = u.replace(/^(\/solvit-api)+/i, "/solvit-api");

  const origin = getApiOrigin();

  if (u.startsWith("/solvit-api/")) {
    const host = window.location.hostname;
    const isLocal = host === "localhost" || host === "127.0.0.1";
    return isLocal ? `http://82.165.15.24${u}` : u;
  }

  if (u.startsWith("/uploads/")) return `${origin}${u}`;
  if (u.startsWith("/")) return `${origin}${u}`;
  return `${origin}/${u}`;
}

export default function AdminMedia() {
  const replaceFileRef = useRef(null);

  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");

  const [items, setItems] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [search, setSearch] = useState("");
  const [folderFilter, setFolderFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);

  const [error, setError] = useState("");
  const [uploadMsg, setUploadMsg] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFilename, setEditFilename] = useState("");
  const [editFolder, setEditFolder] = useState("");
  const [editTags, setEditTags] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  const [replaceBusyId, setReplaceBusyId] = useState("");
  const [replaceTarget, setReplaceTarget] = useState(null);

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === customerId) || null,
    [customers, customerId]
  );

  const loadCustomers = async () => {
    setLoadingCustomers(true);
    setError("");
    try {
      const data = await apiFetch("/api/admin/customers");
      setCustomers(Array.isArray(data) ? data : data?.customers || []);
    } catch (e) {
      setCustomers([]);
      setError(e?.message || "Kunden konnten nicht geladen werden.");
    } finally {
      setLoadingCustomers(false);
    }
  };

  const loadMedia = async (cid) => {
    if (!cid) {
      setItems([]);
      return;
    }

    setLoadingItems(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.set("customerId", cid);
      if (search.trim()) params.set("q", search.trim());
      if (folderFilter) params.set("folder", folderFilter);
      if (tagFilter) params.set("tag", tagFilter);

      let data;
      try {
        data = await apiFetch(`/api/admin/media?${params.toString()}`);
      } catch (err) {
        if (String(err?.message || "").includes("404")) {
          data = await apiFetch(`/api/media?${params.toString()}`);
        } else {
          throw err;
        }
      }

      const list = Array.isArray(data) ? data : data?.media || data?.items || [];
      setItems(list);
    } catch (e) {
      setItems([]);
      setError(e?.message || "Medien konnten nicht geladen werden.");
    } finally {
      setLoadingItems(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      loadMedia(customerId);
    }, 180);
    return () => clearTimeout(t);
  }, [customerId, search, folderFilter, tagFilter]);

  const allFolders = useMemo(() => {
    const set = new Set();
    items.forEach((item) => {
      const folder = String(item.folder || "").trim();
      if (folder) set.add(folder);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const allTags = useMemo(() => {
    const set = new Set();
    items.forEach((item) => normalizeTags(item).forEach((tag) => set.add(tag)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const visibleItems = useMemo(() => {
    if (!duplicatesOnly) return items;
    return items.filter((item) => Number(item.duplicateCount || 1) > 1);
  }, [items, duplicatesOnly]);

  const duplicateCount = useMemo(
    () => items.filter((item) => Number(item.duplicateCount || 1) > 1).length,
    [items]
  );

  const onUpload = async (e) => {
    e.preventDefault();
    setUploadMsg("");
    setError("");

    if (!customerId) return setUploadMsg("Bitte zuerst einen Kunden auswählen.");
    const file = e.target?.elements?.file?.files?.[0];
    if (!file) return setUploadMsg("Bitte eine Datei auswählen (Bild oder Video).");

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("customerId", String(customerId));
      fd.append("file", file, file.name);
      if (folderFilter) fd.append("folder", folderFilter);
      if (tagFilter) fd.append("tags", JSON.stringify([tagFilter]));

      const token = localStorage.getItem("solvit_auth_token");
      if (!token) throw new Error("Nicht eingeloggt (Token fehlt). Bitte neu einloggen.");

      let res = await fetch(`${getApiOrigin()}/api/admin/media/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      if (res.status === 404) {
        const fd2 = new FormData();
        fd2.append("file", file);
        res = await fetch(`${getApiOrigin()}/api/media/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd2,
        });
      }

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Upload fehlgeschlagen (${res.status})`);
      }

      setUploadMsg("Upload erfolgreich.");
      await loadMedia(customerId);
      e.target.reset();
    } catch (err) {
      setUploadMsg("");
      setError(err?.message || "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (mediaId) => {
    if (!mediaId) return;
    setError("");
    setUploadMsg("");

    try {
      let res = await fetch(`${getApiOrigin()}/api/admin/media/${mediaId}`, {
        method: "DELETE",
        headers: (() => {
          const token = localStorage.getItem("solvit_auth_token");
          return token ? { Authorization: `Bearer ${token}` } : {};
        })(),
      });

      if (res.status === 404) {
        res = await fetch(`${getApiOrigin()}/api/media/${mediaId}`, {
          method: "DELETE",
          headers: (() => {
            const token = localStorage.getItem("solvit_auth_token");
            return token ? { Authorization: `Bearer ${token}` } : {};
          })(),
        });
      }

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Löschen fehlgeschlagen (${res.status})`);
      }

      await loadMedia(customerId);
      setUploadMsg("Datei gelöscht.");
    } catch (e) {
      setError(e?.message || "Löschen fehlgeschlagen.");
    }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setEditFilename(item?.filename || "");
    setEditFolder(item?.folder || "");
    setEditTags(normalizeTags(item).join(", "));
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditingItem(null);
    setEditFilename("");
    setEditFolder("");
    setEditTags("");
    setEditBusy(false);
  };

  const saveEdit = async () => {
    if (!editingItem) return;
    setEditBusy(true);
    setError("");

    try {
      await apiFetch(`/api/admin/media/${editingItem.id}`, {
        method: "PATCH",
        body: {
          filename: editFilename,
          folder: String(editFolder || "").trim(),
          tags: editTags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),
        },
      });

      closeEdit();
      await loadMedia(customerId);
      setUploadMsg("Metadaten gespeichert.");
    } catch (e) {
      setError(e?.message || "Speichern fehlgeschlagen.");
    } finally {
      setEditBusy(false);
    }
  };

  const triggerReplace = (item) => {
    setReplaceTarget(item);
    replaceFileRef.current?.click();
  };

  const onReplacePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !replaceTarget) return;

    setError("");
    setUploadMsg("");
    setReplaceBusyId(replaceTarget.id);

    try {
      const token = localStorage.getItem("solvit_auth_token");
      if (!token) throw new Error("Nicht eingeloggt (Token fehlt). Bitte neu einloggen.");

      const fd = new FormData();
      fd.append("file", file, file.name);

      let res = await fetch(`${getApiOrigin()}/api/admin/media/${replaceTarget.id}/replace`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      if (res.status === 404) {
        res = await fetch(`${getApiOrigin()}/api/media/${replaceTarget.id}/replace`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
      }

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Ersetzen fehlgeschlagen (${res.status})`);
      }

      const payload = await res.json().catch(() => null);
      await loadMedia(customerId);
      setUploadMsg(`Datei ersetzt (${Number(payload?.replacedInLoops || 0)} Loop-Einträge aktualisiert).`);
    } catch (err) {
      setError(err?.message || "Ersetzen fehlgeschlagen.");
    } finally {
      setReplaceBusyId("");
      setReplaceTarget(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <MDTypography variant="h5" fontWeight="medium">
          Medien (Admin)
        </MDTypography>
      </MDBox>

      <input ref={replaceFileRef} type="file" accept="image/*,video/*" style={{ display: "none" }} onChange={onReplacePicked} />

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={2}>
              <MDTypography variant="h6" fontWeight="medium" mb={1}>
                Kunde auswählen
              </MDTypography>

              {loadingCustomers ? (
                <MDTypography variant="button" color="text">
                  Lädt...
                </MDTypography>
              ) : (
                <TextField
                  fullWidth
                  select
                  label="Kunde"
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                >
                  <MenuItem value="">Bitte wählen</MenuItem>
                  {customers.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.customerNumber} - {c.companyName}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {selectedCustomer && (
                <MDBox mt={2}>
                  <MDTypography variant="button" color="text">
                    Ausgewählt: <strong>{selectedCustomer.customerNumber}</strong>
                  </MDTypography>
                </MDBox>
              )}

              {error && (
                <MDTypography variant="button" color="error" mt={1}>
                  {error}
                </MDTypography>
              )}
            </MDBox>
          </Card>

          <Card style={{ marginTop: 16 }}>
            <MDBox p={2} component="form" onSubmit={onUpload}>
              <MDTypography variant="h6" fontWeight="medium" mb={1}>
                Upload
              </MDTypography>

              <input
                name="file"
                type="file"
                accept="image/*,video/*"
                style={{ display: "block", marginBottom: 12 }}
              />

              <MDButton type="submit" variant="gradient" color="info" disabled={uploading}>
                {uploading ? "Lädt hoch..." : "Datei hochladen"}
              </MDButton>

              {uploadMsg && (
                <MDTypography variant="button" color="success" ml={2}>
                  {uploadMsg}
                </MDTypography>
              )}
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <MDBox p={2}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Dateien
              </MDTypography>

              {!customerId ? (
                <MDTypography variant="button" color="text">
                  Bitte zuerst einen Kunden auswählen.
                </MDTypography>
              ) : (
                <>
                  <Grid container spacing={2} mb={1}>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        label="Suche"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon fontSize="small">search</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth select label="Ordner" value={folderFilter} onChange={(e) => setFolderFilter(e.target.value)}>
                        <MenuItem value="">Alle</MenuItem>
                        {allFolders.map((folder) => (
                          <MenuItem key={folder} value={folder}>
                            {folder}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField fullWidth select label="Tag" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
                        <MenuItem value="">Alle</MenuItem>
                        {allTags.map((tag) => (
                          <MenuItem key={tag} value={tag}>
                            {tag}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControlLabel
                        control={<Switch checked={duplicatesOnly} onChange={(e) => setDuplicatesOnly(e.target.checked)} />}
                        label="Duplikate"
                      />
                    </Grid>
                  </Grid>

                  <MDTypography variant="caption" color={duplicateCount > 0 ? "warning" : "text"}>
                    {duplicateCount} Duplikat-Datei{duplicateCount === 1 ? "" : "en"} erkannt
                  </MDTypography>

                  {loadingItems ? (
                    <MDTypography variant="button" color="text" mt={2}>
                      Lädt...
                    </MDTypography>
                  ) : visibleItems.length === 0 ? (
                    <MDTypography variant="button" color="text" mt={2}>
                      Keine Medien vorhanden.
                    </MDTypography>
                  ) : (
                    <Grid container spacing={2} mt={0.5}>
                      {visibleItems.map((m) => {
                        const rawUrl = m.url || m.fileUrl || m.publicUrl;
                        const url = toAbsoluteUrl(rawUrl);
                        const tags = normalizeTags(m);
                        return (
                          <Grid item xs={12} sm={6} md={4} key={m.id}>
                            <Card style={{ overflow: "hidden", border: Number(m.duplicateCount || 1) > 1 ? "1px solid #f39c12" : "none" }}>
                              <MDBox p={1} display="flex" justifyContent="space-between" alignItems="center">
                                <MDTypography variant="button" fontWeight="medium" sx={{ maxWidth: 160 }} noWrap>
                                  {m.filename || "Datei"}
                                </MDTypography>

                                <MDBox>
                                  <Tooltip title="Metadaten bearbeiten">
                                    <IconButton size="small" onClick={() => openEdit(m)}>
                                      <Icon fontSize="small">edit</Icon>
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Datei überall ersetzen">
                                    <span>
                                      <IconButton size="small" onClick={() => triggerReplace(m)} disabled={replaceBusyId === m.id}>
                                        <Icon fontSize="small">published_with_changes</Icon>
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Löschen">
                                    <IconButton size="small" onClick={() => onDelete(m.id)}>
                                      <Icon fontSize="small">delete</Icon>
                                    </IconButton>
                                  </Tooltip>
                                </MDBox>
                              </MDBox>

                              <MDBox px={1} pb={1}>
                                {isImage(m.mimeType) && url ? (
                                  <img
                                    src={`${url}?v=${m.id}`}
                                    alt={m.filename || "media"}
                                    style={{ width: "100%", height: 140, objectFit: "cover", borderRadius: 8 }}
                                  />
                                ) : isVideo(m.mimeType) && url ? (
                                  <video
                                    src={`${url}?v=${m.id}`}
                                    controls
                                    style={{ width: "100%", height: 140, objectFit: "cover", borderRadius: 8 }}
                                  />
                                ) : url ? (
                                  <MDButton
                                    variant="outlined"
                                    size="small"
                                    component="a"
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    fullWidth
                                  >
                                    Öffnen
                                  </MDButton>
                                ) : (
                                  <MDTypography variant="button" color="text">
                                    Keine URL
                                  </MDTypography>
                                )}

                                <MDBox mt={1}>
                                  <MDTypography variant="caption" color="text" display="block">
                                    {formatBytes(m.sizeBytes)} • in {Number(m.usageCount || 0)} Loop(s)
                                  </MDTypography>
                                  <MDTypography variant="caption" color="text" display="block">
                                    Ordner: {m.folder || "-"}
                                  </MDTypography>
                                  {Number(m.duplicateCount || 1) > 1 && (
                                    <MDTypography variant="caption" color="warning" display="block">
                                      Duplikat erkannt ({m.duplicateCount}x)
                                    </MDTypography>
                                  )}
                                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" mt={0.5}>
                                    {tags.map((tag) => (
                                      <Chip key={`${m.id}-${tag}`} label={tag} size="small" />
                                    ))}
                                  </Stack>
                                </MDBox>
                              </MDBox>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </>
              )}
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>Medium bearbeiten</DialogTitle>
        <DialogContent>
          <MDBox pt={1} display="grid" gap={2}>
            <TextField label="Dateiname" fullWidth value={editFilename} onChange={(e) => setEditFilename(e.target.value)} />
            <TextField label="Ordner" fullWidth value={editFolder} onChange={(e) => setEditFolder(e.target.value)} />
            <TextField
              label="Tags"
              fullWidth
              value={editTags}
              onChange={(e) => setEditTags(e.target.value)}
              helperText="Kommagetrennt"
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="dark" onClick={closeEdit}>
            Abbrechen
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={saveEdit} disabled={editBusy}>
            {editBusy ? "Speichert..." : "Speichern"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}
